import { Logger, Utils, StringUtils, ArrayUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../../ComponentTypes";
import { Tweens } from "../../Effects/Tweens";
import { SwitchStyle } from "../Styles/SwitchStyle";
import { StyleManager } from "../../Managers/StyleManager";
import { IElement } from "../Interfaces/IElement";
import { Attributes } from "../../../Utils/Attributes";

/**
 * ShapeElement allows you to display vector art in the display list.
 * Example:
 *   <ShapeElement Class="ShapeElement" X="0" Y="0" Width="100" Height="100" Color="FF0000" Mask="ShapeElement" />
 *
 * @class ShapeElement
 * @extends PIXI.Graphics
 * @implements IElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ShapeElement extends PIXI.Graphics implements IElement {
    private _uid: string;
    private _color: number = 0xff0000;
    private _x: number = 0;
    private _y: number = 0;

    private _width: number;
    private _height: number;
    private _lineWidth: number;
    private _lineColor: number;
    private _fillAlpha: number;

    protected style: SwitchStyle;
    private _tweenEngine: string;

    constructor() {
        super();

        this._uid = `${this.componentName}_${Utils.generateUuid()}`;
    }

    get tweenEngine(): string {
        return this._tweenEngine;
    }

    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        Tweens.removeTweens(this);
    }

    public create(node: Element): void {
        this.createByNode(node);
        this.updateByNode(node);
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    createByNode(node: Element): void {
        if (this.name == null) {
            this.name = node.nodeName;
        }

        if (node.hasAttribute("X")) {
            this._x = parseInt(node.getAttribute("X"));
        }

        if (node.hasAttribute("Y")) {
            this._y = parseInt(node.getAttribute("Y"));
        }

        if (node.hasAttribute("Style")) {
            this.style = <SwitchStyle>StyleManager.Styles[node.getAttribute(ShapeElementAttributes.STYLE)];
            if (this.style != null) {
                //console.log('test shapeElement style');
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name: " + node.getAttribute(ShapeElementAttributes.STYLE));
                }
            }
        }

        if (node.hasAttribute(ShapeElementAttributes.WIDTH)) {
            this._width = parseInt(node.getAttribute(ShapeElementAttributes.WIDTH));
        } else {
            if (DEBUG) {
                this.log("ERROR " + ShapeElementAttributes.WIDTH + " attribute doesn't exits");
            }
        }

        if (node.hasAttribute(ShapeElementAttributes.HEIGHT)) {
            this._height = parseInt(node.getAttribute(ShapeElementAttributes.HEIGHT));
        } else {
            if (DEBUG) {
                this.log("ERROR " + ShapeElementAttributes.HEIGHT + " attribute doesn't exits");
            }
        }

        this._color = Attributes.assignIntAttribute(node, ShapeElementAttributes.COLOR, this._color, 16);
        this._fillAlpha = Attributes.assignFloatAttribute(node, ShapeElementAttributes.FILL_ALPHA, this._fillAlpha);
        this._lineWidth = Attributes.assignIntAttribute(node, ShapeElementAttributes.LINE_WIDTH, this._lineWidth);
        this._lineColor = Attributes.assignIntAttribute(node, ShapeElementAttributes.LINE_COLOR, this._lineColor, 16);

        if (this._width != null && this._height != null) {
            this.clear();
            this.beginFill(this._color, this._fillAlpha);
            this.lineStyle(this._lineWidth, this._lineColor);
            this.drawRect(this._x, this._y, this._width, this._height);
            this.endFill();
        }

        /*if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.createElement(this, this.getDebugInfo());
            }
        }*/
    }

    /**
     * @param node
     */
    public updateByNode(node: Element): void {
        Tweens.removeTweens(this);

        if (node.hasAttribute(ShapeElementAttributes.X)) {
            this.position.x = parseInt(node.getAttribute(ShapeElementAttributes.X));
        }

        if (node.hasAttribute(ShapeElementAttributes.Y)) {
            this.position.y = parseInt(node.getAttribute(ShapeElementAttributes.Y));
        }

        if (node.hasAttribute(ShapeElementAttributes.ALPHA)) {
            this.alpha = parseFloat(node.getAttribute(ShapeElementAttributes.ALPHA));
        }

        if (node.hasAttribute(ShapeElementAttributes.MASK)) {
            let next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute(ShapeElementAttributes.MASK)) {
                    let shapeElement: ShapeElement = new ShapeElement();
                    shapeElement.create(next);
                    this.mask = shapeElement;
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(ShapeElementAttributes.MASK));
                }
            }
        }

        if (node.hasAttribute(ShapeElementAttributes.VISIBLE)) {
            this.visible = StringUtils.parseBoolean(node.getAttribute(ShapeElementAttributes.VISIBLE));
        }

        if (node.hasAttribute(ShapeElementAttributes.ALPHA_TWEEN)) {
            let alphaTween: Array<number> = ArrayUtils.numberArray(
                node.getAttribute(ShapeElementAttributes.ALPHA_TWEEN),
            );
            let ease: any;

            if (alphaTween != null) {
                ease = null;
                if (node.hasAttribute(ShapeElementAttributes.ALPHA_TWEEN_EASE)) {
                    ease = node.getAttribute(ShapeElementAttributes.ALPHA_TWEEN_EASE);
                }

                let tweenDelay: number = null;
                if (node.hasAttribute(ShapeElementAttributes.ALPHA_TWEEN_DELAY)) {
                    tweenDelay = parseInt(node.getAttribute(ShapeElementAttributes.ALPHA_TWEEN_DELAY));
                }

                let tweenDuration: number = null;
                if (node.hasAttribute(ShapeElementAttributes.ALPHA_TWEEN_DURATION)) {
                    tweenDuration = parseInt(node.getAttribute(ShapeElementAttributes.ALPHA_TWEEN_DURATION));
                }

                Tweens.alphaTween(this, {
                    alphas: alphaTween,
                    tweenEase: ease,
                    tweenDuration: tweenDuration,
                    tweenDelay: tweenDelay,
                });
            } else {
                if (DEBUG) {
                    this.log("WARNING wrong parameters for " + ShapeElementAttributes.ALPHA_TWEEN);
                }
            }
        }

        this.runPositionTween(node);

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        /*if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }*/
    }

    /**
     * Runs the position tween
     * @method runPositionTween
     * @param node
     * @returns {boolean}
     */
    public runPositionTween(node: Element): boolean {
        if (
            !node.hasAttribute(ShapeElementAttributes.POSITION_TWEEN_X) ||
            !node.hasAttribute(ShapeElementAttributes.POSITION_TWEEN_Y)
        ) {
            return false;
        }

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        let positionTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ShapeElementAttributes.POSITION_TWEEN_POSITION,
            null,
        );
        if (positionTweenPosition == ShapeElementAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(ShapeElementAttributes.POSITION_TWEEN_X)).map(
                (value) => value + this.position.x,
            );
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(ShapeElementAttributes.POSITION_TWEEN_Y)).map(
                (value) => value + this.position.y,
            );
        } else {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(ShapeElementAttributes.POSITION_TWEEN_X));
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(ShapeElementAttributes.POSITION_TWEEN_Y));
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ShapeElementAttributes.POSITION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ShapeElementAttributes.POSITION_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, ShapeElementAttributes.POSITION_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ShapeElementAttributes.POSITION_TWEEN_LOOP,
            null,
        );

        Tweens.positionTween(this, { tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @retutn void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Log error into console
     * @param s
     */
    protected logError(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }

    clone(): ShapeElement {
        var element: ShapeElement = new ShapeElement();

        element.clear();
        element.beginFill(this._color, this._fillAlpha);
        element.lineStyle(this._lineWidth, this._lineColor);
        element.drawRect(this._x, this._y, this._width, this._height);
        element.endFill();

        return element;
    }
}

export const ShapeElementAttributes = {
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    /*
     * Tween alpha of this display object with children.
     * For duration use AlphaTweenDuration (number - frames).
     * For ease use AlphaTwenEase (string - name of easeing function).
     * @property AlphaTween
     * @type Array<numbers> - consist of two values (start alpha; end alpha) or three values (start alpha, middle alpha, end alpha)
     * @default null
     */
    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",

    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_DELAY: "PositionTweenDelay",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },
    POSITION_TWEEN_LOOP: "PositionTweenLoop",
    /*
     * Shape's fill color.
     * @property Color
     * @type hexadecimal
     * @default ff0000
     */
    COLOR: "Color",
    FILL_ALPHA: "FillAlpha",
    /*
     * Shape's height.
     * @property Height
     * @type number
     * @default null
     */
    HEIGHT: "Height",
    LINE_COLOR: "LineColor",
    LINE_WIDTH: "LineWidth",
    /*
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type string
     * @default null
     */
    MASK: "Mask",
    STYLE: "Style",
    /*
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /*
     * Shape's width.
     * @property Width
     * @type number
     * @default null
     */
    WIDTH: "Width",
    /*
     * The X (horizontal) position of the shape, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * The Y (vertical) position of the shape, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ShapeElement.ts
 **/